import type { CSSProperties } from "react";
import { color, motion as motionTokens, radius, space } from "@tokens/index";

/**
 * Shared geometry for the list work surfaces (queue rail, queue tree, work
 * items, entity workflows).
 *
 * Every row is flat. Depth comes from tone and edge, never from shadow.
 * Two row materials exist:
 *  - `transparent`: rail rows on the canvas. Hover tints with `bg.hover`.
 *  - `filled`: content rows one tonal step above the canvas with a hairline
 *    border. Hover steps to `bg.surface2`.
 * A selected row fills with `bg.selected` and shows a 3px accent bar on the
 * leading edge.
 */

export type ListRowMaterial = "transparent" | "filled";

export interface ListRowState {
  readonly hovered: boolean;
  readonly selected: boolean;
}

export const listStack: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: space[1],
};

export function listRowBackground(material: ListRowMaterial, state: ListRowState): string {
  if (state.selected) return color.bg.selected;
  if (material === "filled") return state.hovered ? color.bg.surface2 : color.bg.surface;
  return state.hovered ? color.bg.hover : "transparent";
}

export function listRow(material: ListRowMaterial, state: ListRowState): CSSProperties {
  return {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: space[3],
    width: "100%",
    // Content owns the height. The stack scrolls. A row must not shrink
    // to share leftover viewport with its siblings — that collapse is
    // what paints one card's text through the next.
    height: "auto",
    minHeight: 40,
    flexShrink: 0,
    overflow: "hidden",
    padding: `${space[2]} ${space[3]}`,
    borderRadius: radius.lg,
    border: `1px solid ${material === "filled" ? color.border.subtle : "transparent"}`,
    background: listRowBackground(material, state),
    color: color.fg.default,
    textAlign: "left",
    boxSizing: "border-box",
    transition: `background ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
  };
}

/** The 3px accent bar of a selected row. The host row is `position: relative`. */
export const selectionBar: CSSProperties = {
  position: "absolute",
  left: 0,
  top: space[2],
  bottom: space[2],
  width: 3,
  borderRadius: radius.pill,
  background: color.accent.primary,
  pointerEvents: "none",
};

/** A text column inside a row. `minWidth: 0` lets the children truncate. */
export const rowMain: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[1],
  flex: 1,
  minWidth: 0,
};

/** One muted metadata line under a title. Wraps on narrow rows. */
export const rowMeta: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[2],
  flexWrap: "wrap",
  minWidth: 0,
};

/** A 40px square that holds a leading glyph and aligns with `IconButton`. */
export const leadingSlot: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  flexShrink: 0,
};

export const errorBlock: CSSProperties = {
  padding: `${space[3]} ${space[4]}`,
  borderRadius: radius.lg,
  border: `1px solid color-mix(in oklch, ${color.accent.danger} 40%, transparent)`,
  background: color.status.dangerBg,
  color: color.fg.default,
};

/** Multi-line clamp for a title on the compact class. */
export function clampLines(lines: number): CSSProperties {
  return {
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minWidth: 0,
  };
}
